import React, { useEffect, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { differenceInMilliseconds, formatDistanceStrict } from 'date-fns';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import {
	type PreloadedQuery,
	graphql,
	useFragment,
	usePreloadedQuery,
	useQueryLoader,
} from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';
import { SUPPORT_DOCS_LINK_URL } from '../../common/constants.tsx';
import { useDeploymentsRoute } from '../../common/ui/deployments-page-link/index.tsx';
import type { latestDeployment_softwareReleasesLaunchCard_jiraVersion$key } from './__generated__/latestDeployment_softwareReleasesLaunchCard_jiraVersion.graphql';
import type { latestDeployment_softwareReleasesLaunchCard_LatestDeploymentContent_jiraVersion$key } from './__generated__/latestDeployment_softwareReleasesLaunchCard_LatestDeploymentContent_jiraVersion.graphql';
import type { latestDeploymentForVersionPreconditionQuery } from './__generated__/latestDeploymentForVersionPreconditionQuery.graphql';
import LatestDeploymentForVersionQuery, {
	type latestDeploymentForVersionQuery,
} from './__generated__/latestDeploymentForVersionQuery.graphql';
import { DeploymentTooltipContent } from './deployment-tooltip-content/index.tsx';
import { PulseIcon } from './icon/PulseIcon.tsx';
import messages from './messages.tsx';
import {
	ProvidersList,
	useLoadProvidersListQuery,
} from './providers-list-tooltip-content/index.tsx';

type InnerProps = {
	jiraVersion: latestDeployment_softwareReleasesLaunchCard_LatestDeploymentContent_jiraVersion$key;
	queryReference: PreloadedQuery<latestDeploymentForVersionQuery>;
};

export const LatestDeploymentContent = ({
	jiraVersion,
	queryReference,
}: InnerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const deploymentsRoute = useDeploymentsRoute();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment latestDeployment_softwareReleasesLaunchCard_LatestDeploymentContent_jiraVersion on JiraVersion {
				id @required(action: THROW)
				name @required(action: THROW)
				project @required(action: THROW) {
					key @required(action: THROW)
				}
				...deploymentTooltipContent_softwareReleasesLaunchCard_jiraVersion
			}
		`,
		jiraVersion,
	);

	const isMoreThanAWeekApart = (a: Date, b: Date): boolean => {
		const oneWeekInMilliseconds = 1000 * 60 * 60 * 24 * 7;

		return differenceInMilliseconds(a, b) > oneWeekInMilliseconds;
	};

	const data = usePreloadedQuery<latestDeploymentForVersionQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query latestDeploymentForVersionQuery($from: ID!) {
				devOps @required(action: THROW) {
					ariGraph @required(action: THROW) {
						relationships(
							type: "version-associated-deployment"
							from: $from
							first: 1
							sort: { lastUpdatedSortDirection: DESC }
						) @required(action: THROW) {
							edges @required(action: THROW) {
								node @required(action: THROW) {
									to @required(action: THROW) {
										data @required(action: THROW) {
											... on DeploymentSummary {
												lastUpdated @required(action: THROW)
												...deploymentTooltipContent_softwareReleasesLaunchCard_deployment
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		queryReference,
	);

	const deploymentNodes = data.devOps.ariGraph.relationships.edges
		.filter(Boolean)
		.map((edge) => edge.node);

	const renderLink = (content: ReactNode, hasNoRecentDeployments: boolean): ReactNode =>
		hasNoRecentDeployments ? (
			<StyledLink
				href={SUPPORT_DOCS_LINK_URL}
				onClick={() => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'link',
						}),
						'latestDeploymentInVersion',
					);
				}}
				target="_blank"
			>
				{content}
			</StyledLink>
		) : (
			<StyledLink
				onClick={() => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'link',
						}),
						'latestDeploymentInVersion',
					);
				}}
				prefetch="hover"
				params={{
					projectKey: version.project.key,
				}}
				query={{ startDate: '-3m', endDate: 'now', fixVersions: version.name }}
				to={deploymentsRoute}
			>
				{content}
			</StyledLink>
		);

	const latestDeployment = deploymentNodes[0]?.to.data;
	const hasNoDeploymentInLastWeek = latestDeployment?.lastUpdated
		? isMoreThanAWeekApart(new Date(), new Date(latestDeployment.lastUpdated))
		: true;

	let linkContent: ReactNode | null = null;

	if (latestDeployment == null) {
		linkContent = formatMessage(messages.noDeployments);
	} else if (latestDeployment.lastUpdated !== undefined) {
		if (hasNoDeploymentInLastWeek) {
			linkContent = formatMessage(messages.noDeployments);
		} else {
			linkContent = (
				<>
					{formatMessage(messages.lastDeployment)}
					{formatDistanceStrict(new Date(latestDeployment.lastUpdated), new Date(), {
						addSuffix: true,
						roundingMethod: 'floor',
					})}
				</>
			);
		}
	}

	const [providersListQuery, loadProvidersListQuery] = useLoadProvidersListQuery();

	return (
		<Tooltip
			// @ts-expect-error - TS3233 - unsure why it doesn't like my tooltip :|
			component={CustomTooltip}
			content={
				hasNoDeploymentInLastWeek && providersListQuery ? (
					<ProvidersList queryReference={providersListQuery} />
				) : (
					<DeploymentTooltipContent jiraVersion={version} deployment={latestDeployment} />
				)
			}
			onShow={(analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(
					analyticsEvent,
					hasNoDeploymentInLastWeek && providersListQuery
						? 'latestDeploymentInVersionProvidersTooltip'
						: 'latestDeploymentInVersionDetailsTooltip',
				);
			}}
		>
			{(tooltipProps) => (
				<Wrapper
					{...tooltipProps}
					onMouseOver={(event) => {
						tooltipProps.onMouseOver(event);
						if (hasNoDeploymentInLastWeek) {
							loadProvidersListQuery();
						}
					}}
				>
					<PulseIcon />
					{linkContent &&
						renderLink(linkContent, latestDeployment == null || hasNoDeploymentInLastWeek)}
				</Wrapper>
			)}
		</Tooltip>
	);
};

type Props = {
	jiraVersion: latestDeployment_softwareReleasesLaunchCard_jiraVersion$key;
	preconditionQueryRef: PreloadedQuery<latestDeploymentForVersionPreconditionQuery>;
};

export const LatestDeployment = ({
	jiraVersion,
	preconditionQueryRef,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment latestDeployment_softwareReleasesLaunchCard_jiraVersion on JiraVersion {
				id @required(action: THROW)
				...latestDeployment_softwareReleasesLaunchCard_LatestDeploymentContent_jiraVersion
			}
		`,
		jiraVersion,
	);

	const {
		jira: { deploymentsFeaturePreconditionByProjectKey },
	} = usePreloadedQuery<latestDeploymentForVersionPreconditionQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query latestDeploymentForVersionPreconditionQuery($cloudId: ID!, $projectKey: String!) {
				jira @required(action: THROW) {
					deploymentsFeaturePreconditionByProjectKey(cloudId: $cloudId, projectKey: $projectKey)
				}
			}
		`,
		preconditionQueryRef,
	);

	const [queryRef, loadQuery] = useQueryLoader<latestDeploymentForVersionQuery>(
		LatestDeploymentForVersionQuery,
	);

	useEffect(() => {
		if (deploymentsFeaturePreconditionByProjectKey === 'ALL_SATISFIED') {
			loadQuery({ from: version.id });
		}
	}, [deploymentsFeaturePreconditionByProjectKey, version.id, loadQuery]);

	return deploymentsFeaturePreconditionByProjectKey === 'ALL_SATISFIED' ? (
		<Placeholder
			name="latest-deployment-content"
			fallback={
				<Wrapper>
					<PulseIcon />
					{formatMessage(messages.loading)}
				</Wrapper>
			}
		>
			{queryRef && <LatestDeploymentContent jiraVersion={version} queryReference={queryRef} />}
		</Placeholder>
	) : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled(Link)({
	color: token('color.text'),
	'&:hover, &:focus, &:focus-visible, &:active': {
		color: token('color.text'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	alignItems: 'center',

	background: `${token('color.background.neutral')}`,
	display: 'flex',
	gap: token('space.100'),
	paddingTop: token('space.200'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomTooltip = styled(TooltipPrimitive)({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
	boxSizing: 'content-box',

	backgroundColor: `${token('elevation.surface')}`,
});
